import { type FigureInfo } from '~/utils'

import { expandCustomerAttributeGroupId } from '../atoms'

import { buildAllowedQuestionIdsForBrand } from './buildTableElements'

// Pure task-planning module: NO React hooks, NO graphql/save side effects, and —
// critically — NO engine result. The render-task COUNT and identity are derived
// purely from WIZARD INPUT so they are deterministic and verifiable before the
// engine runs. The engine result is consumed ONLY when BUILDING each task's table
// content (see useRenderAndSaveDocumentImages), never here.

export type RenderKind = 'customer' | 'customized' | 'standard'

export const kindToPathKey: Record<RenderKind, FigureInfo['pathKey']> = {
  customer: 'customerAnalysis',
  customized: 'customizedAnalysis',
  standard: 'standardAnalysis',
}

// A pure descriptor of one table to render. Carries NO tableElement — the element
// is built later from the engine result, keyed by (filterIndex, kind, role,
// brandId, segmentMode).
export type RenderTask = {
  allowedQuestionIds?: string[] // standard/question-axis only (matrix per-brand filter), wizard-derived
  // What a 'standard' task is analysed BY; absent means the question axis. Optional because
  // it is meaningless for customer/customized tables.
  // Deliberately NOT a new RenderKind: a customer-attribute slot renders the same
  // StandardAnalysisPage-shaped table, so keeping kind='standard' leaves kindToPathKey, the
  // document section bucketing/ordering, the image title and the outputType all unchanged.
  axis?: 'customerAttribute' | 'question'
  brandId?: number // undefined for customer/customized (no brand); brand.id for standard
  brandName?: string // undefined for customer/customized (no brand); brand name for standard
  // customerAttribute axis only. Carried so the saved figure's deep link restores
  // StandardAnalysisPage with these attributes selected.
  customerAttributeIds?: string[]
  filterIndex: number
  filterName: string
  // Brand-group-wide tasks only: the 自社 brand this copy is FRAMED for (the AI comment
  // interprets the table from its viewpoint). Deliberately NOT reusing brandId/brandName —
  // brandId === null is the structural sentinel routing an image into the brand-common
  // A-doc (see buildDocumentHtml) and into the group-wide restore params, so borrowing it
  // here would silently move 顧客分布 into a per-brand B-doc.
  framingBrandId?: number
  framingBrandName?: string
  // Explicit discriminant computed once during planning. The build/render loop
  // branches on `kind` (NOT a string prefix on outputType); `role` carries the
  // bare wizard-state key for questionId resolution and output lookup.
  kind: RenderKind
  // Deterministic unique id for this output, minted once here and carried through
  // (SavedImageEntry → comment send → echo → direct lookup). Equals the output
  // identity tuple, so the three independent planRenderTasks callers agree.
  outputKey: string
  role?: string // bare wizard role/customKey (standard & customized)
  // customized only: this role's RAW wizard selection, before the sub-question
  // expansion useGenerateBasicOutputs applies for the engine request. Carried so the
  // table builder can partition matrix vs 連続設問 from the SELECTION — the engine
  // result cannot tell a lone matrix sub-question apart from a selected parent
  // (see findSelectedMatrixParents).
  selectedQuestionIds?: string[]
  segmentMode: '5segs' | '9segs'
}

type PlanRenderTasksContext = {
  // Gates the 自社 fan-out below. The ONLY thing that differs between the copies of a
  // group-wide table is its AI comment, so with comments off they would be byte-identical
  // sections costing an extra offscreen render and upload each.
  aiCommentsEnabled: boolean
  brandIdToOriginalIndex: Map<number, number>
  // expands a customer-attribute-axis role's GROUP id into its attribute ids
  customerAttributeGroups: {
    customerAttributes: { id: string }[]
    id: string
  }[]
  // role -> axis; an absent KEY means the question axis
  customRoleAxes: Record<string, 'customerAttribute' | 'question' | undefined>
  filterPatternDrafts: { name: string }[] // count + names (filter patterns)
  getBrandAligned: (role: string) => boolean
  groupBrands: { id: number; name: string; number: number }[] // full brand group from survey
  // Gates 'customer' (9segs顧客分布) and 'customized' (カスタマイズ分析) tasks —
  // these are brand-group-wide outputs, independent of selectedBrandIds.
  isEntireBrandGroupSelected: boolean
  // Brands flagged 自社. Each one gets its own copy of the role-aware brand-group-wide
  // tables so its AI comment can be framed from that brand's viewpoint.
  ownBrandIds: readonly number[]
  // role -> selected ids. A customer-attribute-axis role holds a single GROUP id here.
  questionAssignments: Record<string, string[]>
  questions: {
    mt_questions: string[]
    question_id: string
    question_text: string
  }[]
  selectedBrandIds: number[] // explicit; may be empty when isEntireBrandGroupSelected is true
}

// The customized-analysis roles the wizard emits (npiDriver + importance only).
const CUSTOMIZED_ROLES = ['npiDriver', 'importance']

const makeOutputKey = (
  filterIndex: number,
  kind: RenderKind,
  role: string | undefined,
  brandId: number | undefined,
  segmentMode: string,
  framingBrandId: number | undefined,
): string =>
  `${filterIndex}:${kind}:${role ?? ''}:${brandId ?? ''}:${segmentMode}:${framingBrandId ?? ''}`

export const planRenderTasks = (ctx: PlanRenderTasksContext): RenderTask[] => {
  const {
    aiCommentsEnabled,
    brandIdToOriginalIndex,
    customerAttributeGroups,
    customRoleAxes,
    filterPatternDrafts,
    getBrandAligned,
    groupBrands,
    isEntireBrandGroupSelected,
    ownBrandIds,
    questionAssignments,
    questions,
    selectedBrandIds,
  } = ctx

  // Active standard-analysis roles: each questionAssignments entry with ≥1 id.
  // Custom roles live in the same map (key presence = active), so the single
  // loop covers builtin AND custom roles in insertion order. A customer-attribute-axis
  // role holds one GROUP id, expanded here into that group's attribute ids.
  const activeRoles: {
    axis: 'customerAttribute' | 'question'
    customerAttributeIds?: string[]
    role: string
    selectedQuestionIds: string[]
  }[] = []
  for (const [role, selectedIds] of Object.entries(questionAssignments)) {
    if (selectedIds.length === 0) {
      continue
    }
    if (customRoleAxes[role] === 'customerAttribute') {
      activeRoles.push({
        axis: 'customerAttribute',
        customerAttributeIds: expandCustomerAttributeGroupId(
          customerAttributeGroups,
          selectedIds[0],
          role,
        ),
        role,
        selectedQuestionIds: [],
      })
      continue
    }
    activeRoles.push({
      axis: 'question',
      role,
      selectedQuestionIds: selectedIds,
    })
  }

  // Wizard standard-analysis brand set: the user's explicitly selected brands,
  // filtered to the group in original-index order. The wizard requires
  // selectedBrandIds OR isEntireBrandGroupSelected — both empty/false is the one
  // invalid combination (BrandSelectionStep blocks advancing otherwise), so there
  // is NO "empty means full group" convention — assert the contract.
  if (selectedBrandIds.length === 0 && !isEntireBrandGroupSelected) {
    throw new Error(
      'planRenderTasks: selectedBrandIds and isEntireBrandGroupSelected must not both be empty',
    )
  }
  const targetBrands = groupBrands.filter((b) =>
    selectedBrandIds.includes(b.id),
  )

  // One copy of each role-aware group-wide table per 自社 brand, in brand-group display
  // order. The single `undefined` slot when no 自社 is flagged (or comments are off) is what
  // keeps such a run at its previous output count — mapping to an empty list would drop
  // 顧客分布 entirely instead of emitting the category-framed copy.
  const framingBrands = groupBrands.filter((b) => ownBrandIds.includes(b.id))
  const framingSlots: ({ id: number; name: string } | undefined)[] =
    aiCommentsEnabled && 0 < framingBrands.length ? framingBrands : [undefined]

  const tasks: RenderTask[] = []

  for (const [filterIndex, draft] of filterPatternDrafts.entries()) {
    const filterName = draft.name

    // Customer: 2 tables (one per segment view) per 自社 brand, all brands in one grid.
    // The grid itself is identical across 自社 copies — only the framing (and so the AI
    // comment) differs. Brand-group-wide — only planned when the group itself is targeted.
    if (isEntireBrandGroupSelected) {
      for (const framingBrand of framingSlots) {
        for (const segmentMode of ['5segs', '9segs'] as const) {
          tasks.push({
            filterIndex,
            filterName,
            framingBrandId: framingBrand?.id,
            framingBrandName: framingBrand?.name,
            kind: 'customer',
            outputKey: makeOutputKey(
              filterIndex,
              'customer',
              undefined,
              undefined,
              segmentMode,
              framingBrand?.id,
            ),
            segmentMode,
          })
        }
      }
    }

    // Standard: per active role × per target brand × 2 segment views. The
    // customer-attribute axis is per-brand too — its table's columns are ONE brand's
    // segments, so a single all-brands task would silently render just one of them.
    for (const {
      axis,
      customerAttributeIds,
      role,
      selectedQuestionIds,
    } of activeRoles) {
      const brandAligned = getBrandAligned(role)
      for (const brand of targetBrands) {
        const originalIndex = brandIdToOriginalIndex.get(brand.id) ?? 0
        const allowedQuestionIds =
          axis === 'customerAttribute'
            ? undefined
            : buildAllowedQuestionIdsForBrand(
                selectedQuestionIds,
                questions,
                originalIndex,
                brandIdToOriginalIndex.size,
                brandAligned,
              )
        for (const segmentMode of ['5segs', '9segs'] as const) {
          tasks.push({
            allowedQuestionIds,
            axis,
            brandId: brand.id,
            brandName: brand.name,
            customerAttributeIds,
            filterIndex,
            filterName,
            kind: 'standard',
            outputKey: makeOutputKey(
              filterIndex,
              'standard',
              role,
              brand.id,
              segmentMode,
              undefined,
            ),
            role,
            segmentMode,
          })
        }
      }
    }

    // Customized: 1 table per npiDriver/importance role with ≥1 assigned id.
    // Brand-group-wide like 'customer' above — same gate applies. A
    // customer-attribute-axis slot never gets one (the analyzer skips it too — see
    // buildOutputTasks); planning one would look for an output that was never requested.
    if (isEntireBrandGroupSelected) {
      for (const role of CUSTOMIZED_ROLES) {
        if (customRoleAxes[role] === 'customerAttribute') {
          continue
        }
        const selectedQuestionIds = questionAssignments[role] ?? []
        if (0 < selectedQuestionIds.length) {
          // 重視点 is brand-role-INDEPENDENT server-side (see the analyzer's
          // customizedAnalysisImportance figureSpec), so fanning it out per 自社 would
          // emit byte-identical tables carrying byte-identical comments.
          const roleFramingSlots =
            role === 'npiDriver' ? framingSlots : [undefined]
          for (const framingBrand of roleFramingSlots) {
            tasks.push({
              filterIndex,
              filterName,
              framingBrandId: framingBrand?.id,
              framingBrandName: framingBrand?.name,
              kind: 'customized',
              outputKey: makeOutputKey(
                filterIndex,
                'customized',
                role,
                undefined,
                '9segs',
                framingBrand?.id,
              ),
              role,
              selectedQuestionIds,
              segmentMode: '9segs',
            })
          }
        }
      }
    }
  }

  return tasks
}
